import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SamsaraVehicle } from "@/hooks/useSamsaraVehicles";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Navigation, MapPin, Clock, Container, FileText, WifiOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { isVehicleStale } from "@/lib/samsara-staleness";
import { formatRouteDirection } from "@/lib/heading-utils";

function getSpeedColor(speed: number) {
  if (speed >= 50) return "text-green-500";
  if (speed >= 10) return "text-blue-500";
  return "text-muted-foreground";
}

type CapabilityFilter = "all" | "VO" | "NVO" | "Manual";

interface LiveFleetSidebarProps {
  vehicles: SamsaraVehicle[];
  onVehicleClick?: (vehicle: SamsaraVehicle) => void;
  dataUpdatedAt?: number;
  capabilityFilter?: CapabilityFilter;
  searchQuery?: string;
  selectedVehicleId?: string | null;
}

const CAP_STYLES = {
  VO:     { idle: "bg-blue-500/10 hover:bg-blue-500/20",     active: "ring-blue-500 bg-blue-500/20",     text: "text-blue-500",   badge: "border-blue-500/30 text-blue-500" },
  NVO:    { idle: "bg-amber-500/10 hover:bg-amber-500/20",   active: "ring-amber-500 bg-amber-500/20",   text: "text-amber-500",  badge: "border-amber-500/30 text-amber-500" },
  Manual: { idle: "bg-violet-500/10 hover:bg-violet-500/20", active: "ring-violet-500 bg-violet-500/20", text: "text-violet-500", badge: "border-violet-500/30 text-violet-500" },
} as const;

function OrderLink({ orderId, orderNumber }: { orderId: string; orderNumber: string }) {
  const navigate = useNavigate();
  return (
    <span
      role="link"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        navigate(`/orders/${orderId}`);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          navigate(`/orders/${orderId}`);
        }
      }}
      className="min-w-0 truncate cursor-pointer hover:text-foreground hover:underline"
    >
      {orderNumber}
    </span>
  );
}

export function LiveFleetSidebar({ vehicles, onVehicleClick, dataUpdatedAt, capabilityFilter = "all", searchQuery = "", selectedVehicleId = null }: LiveFleetSidebarProps) {
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    if (dataUpdatedAt) setLastUpdated(new Date(dataUpdatedAt));
  }, [dataUpdatedAt]);

  const effectiveCaps = (v: SamsaraVehicle) =>
    !v.capabilities || v.capabilities.length === 0 ? ["Manual"] : v.capabilities;

  const filtered = useMemo(() => {
    let result = capabilityFilter === "all"
      ? vehicles
      : vehicles.filter((v) => effectiveCaps(v).includes(capabilityFilter));
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((v) =>
        v.name.toLowerCase().includes(q) ||
        (v.currentCity && v.currentCity.toLowerCase().includes(q)) ||
        (v.driver && v.driver.toLowerCase().includes(q)) ||
        (v.trailerId && v.trailerId.toLowerCase().includes(q)) ||
        (v.orderNumber && v.orderNumber.toLowerCase().includes(q))
      );
    }
    return result;
  }, [vehicles, capabilityFilter, searchQuery]);

  return (
    <div className="w-72 bg-card flex flex-col">
      <ScrollArea className="flex-1">
        <div>
          {(() => {
            const sorted = [...filtered].sort((a, b) => {
              const aMoving = a.speed > 0 ? 0 : 1;
              const bMoving = b.speed > 0 ? 0 : 1;
              if (aMoving !== bMoving) return aMoving - bMoving;
              return a.name.localeCompare(b.name);
            });
            return sorted.map((vehicle, i) => (
              <VehicleCard
                key={vehicle.id}
                vehicle={vehicle}
                effectiveCaps={effectiveCaps(vehicle)}
                onClick={() => onVehicleClick?.(vehicle)}
                isLast={i === sorted.length - 1}
                selected={vehicle.id === selectedVehicleId}
              />
            ));
          })()}

          {filtered.length === 0 && (
            <div className="text-center py-8 text-xs text-muted-foreground">
              No vehicles match the current filter
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

function VehicleCard({
  vehicle,
  effectiveCaps,
  onClick,
  isLast = false,
  selected = false,
}: {
  vehicle: SamsaraVehicle;
  effectiveCaps: string[];
  onClick?: () => void;
  isLast?: boolean;
  selected?: boolean;
}) {
  const stale = isVehicleStale(vehicle);
  return (
    <button
      onClick={onClick}
      data-state={selected ? "selected" : undefined}
      className={cn(
        "w-full overflow-hidden text-left px-3 py-2 transition-colors space-y-1.5 group bg-background hover:bg-row-hover",
        "data-[state=selected]:bg-row-selected data-[state=selected]:shadow-[inset_2px_0_0_0_hsl(var(--primary))]",
        !isLast && "border-b",
        stale && "opacity-60"
      )}
    >
      <div className="flex min-w-0 items-center justify-between gap-2">
        <span className="min-w-0 truncate text-sm font-medium group-hover:text-foreground">
          {vehicle.name}
        </span>
        {stale && (
          <Badge
            variant="outline"
            className="shrink-0 text-[10px] px-1.5 py-0 border-amber-500/40 text-amber-500"
          >
            Stale
          </Badge>
        )}
      </div>

      {/* Trailer & Order info */}
      {(vehicle.trailerId || vehicle.orderNumber) && (
        <div className="flex min-w-0 overflow-hidden items-center gap-3 text-[11px] text-muted-foreground">
          {vehicle.trailerId && (
            <span
              className={cn(
                "flex items-center gap-1 overflow-hidden",
                vehicle.orderNumber ? "max-w-[40%] shrink-0" : "min-w-0"
              )}
            >
              <Container className="h-3 w-3 shrink-0" />
              <span className="min-w-0 truncate">{vehicle.trailerId}</span>
            </span>
          )}
          {vehicle.orderNumber && (
            <span className="flex min-w-0 items-center gap-1 overflow-hidden">
              <FileText className="h-3 w-3 shrink-0" />
              {vehicle.orderId ? (
                <OrderLink orderId={vehicle.orderId} orderNumber={vehicle.orderNumber} />
              ) : (
                <span className="min-w-0 truncate">{vehicle.orderNumber}</span>
              )}
            </span>
          )}
        </div>
      )}

      <div className="flex min-w-0 overflow-hidden items-center gap-1 text-[11px] text-muted-foreground">
        <MapPin className="h-3 w-3 shrink-0" />
        <span className="min-w-0 flex-1 truncate">{vehicle.currentCity || "Unknown"}</span>
      </div>

      <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
        {vehicle.speed > 0 && (
          <>
            <span className={cn("flex items-center gap-1 shrink-0", getSpeedColor(vehicle.speed))}>
              <Navigation
                className="h-3 w-3"
                style={
                  vehicle.headingDeg != null
                    ? { transform: `rotate(${vehicle.headingDeg}deg)` }
                    : undefined
                }
              />
              {vehicle.speed} mph
            </span>
            {(() => {
              const route = formatRouteDirection(
                vehicle.currentCity,
                vehicle.headingDeg,
                vehicle.heading
              );
              return route ? (
                <span
                  className="shrink-0 font-medium text-foreground/80"
                  title={`Heading ${vehicle.headingDeg != null ? `${Math.round(vehicle.headingDeg)}° ` : ""}${vehicle.heading}`}
                >
                  {route}
                </span>
              ) : null;
            })()}
          </>
        )}
        <span
          className={cn(
            "flex items-center gap-1 shrink-0",
            stale && "text-amber-500"
          )}
        >
          {stale ? <WifiOff className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
          {vehicle.lastUpdate}
        </span>
      </div>
    </button>
  );
}